import {
  LoginRequestDTO,
  LoginResponseDTO,
  UpdateProfileRequestDTO,
  UserDTO,
  ChangePasswordRequestDTO,
  ForgotPasswordRequestDTO,
  ResetPasswordRequestDTO,
  ForgotPasswordResponseDTO,
  ResetPasswordResponseDTO,
} from '../dto/auth.dto';
import { env } from '@/config/env';
import { oauth2Service } from './oauth2.service';
import { keycloakService } from './keycloak.service';
import { OAuth2UserInfo } from '../../domain/entities/user.entity';
import { httpClient } from '@/shared/infrastructure/http/http-client';

export class AuthApi {
  private readonly baseUrl = '/oauth';

  async login(credentials: LoginRequestDTO): Promise<LoginResponseDTO> {
    try {
      const loginResponse = await oauth2Service.login(credentials.email, credentials.password);

      if (!loginResponse?.success)
        throw new Error("No se a podido iniciar sesión");

      // Save tokens
      oauth2Service.saveTokens(loginResponse);

      // get user information
      const userInfo = await oauth2Service.getUserInfo(loginResponse.access_token);

      // map OAuth2UserInfo to UserDTO
      const user: UserDTO = {
        id: userInfo.sub,
        name: userInfo.name,
        email: userInfo.email,
        username: userInfo.username,
        role: this.mapGroupsToRole(userInfo.groups || []),
        groups: userInfo.groups,
        sub: userInfo.sub,
      };

      return {
        user,
        token: loginResponse.access_token,
        refreshToken: loginResponse.refresh_token,
        expiresIn: Math.floor((loginResponse.expires_at - Date.now()) / 1000),
      };
    } catch (error: any) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        throw new Error('Credenciales incorrectas');
      }
      console.error('Error al iniciar sesión:', error);
      throw error;
    }
  }

  /**
   * map OAuth2 groups to application roles
   */
  private mapGroupsToRole(groups: string[]): string {
    return groups[0] || 'couriers';
  }

  /**
   * Logout
   */
  async logout(): Promise<void> {
    try {
      const tokens = oauth2Service.getTokens();
      if (tokens) {
        // Revocar access token
        if (tokens.access_token) {
          await oauth2Service.revokeToken(tokens.access_token, 'access_token');
        }
        // Revocar refresh token si existe
        if (tokens.refresh_token) {
          await oauth2Service.revokeToken(tokens.refresh_token, 'refresh_token');
        }
      }
    } catch (error) {
      // Si falla la revocación, continuar con el logout local
      console.error('Error al revocar tokens:', error);
    } finally {
      // Limpiar tokens del localStorage
      oauth2Service.clearTokens();
    }
  }

  /**
   * Get the current user
   * @returns 
   */
  async getCurrentUser(): Promise<UserDTO | null> {
    try {
      const tokens = oauth2Service.getTokens();
      if (!tokens) {
        return null;
      }

      // Verificar si el token está expirado
      if (oauth2Service.isTokenExpired(tokens)) {
        // Intentar refrescar el token
        if (tokens.refresh_token) {
          try {
            const newTokens = await oauth2Service.refreshToken(tokens.refresh_token);
            oauth2Service.saveTokens(newTokens);
            // Obtener info del usuario con el nuevo token
            const userInfo = await oauth2Service.getUserInfo(newTokens.access_token);
            return this.mapUserInfoToDTO(userInfo);
          } catch (error) {
            // Si falla el refresh, limpiar y retornar null
            oauth2Service.clearTokens();
            return null;
          }
        } else {
          // No hay refresh token, limpiar y retornar null
          oauth2Service.clearTokens();
          return null;
        }
      }

      // Token válido, obtener info del usuario
      const userInfo = await oauth2Service.getUserInfo(tokens.access_token);
      return this.mapUserInfoToDTO(userInfo);
    } catch (error) {
      console.error('Error al obtener usuario actual:', error);
      oauth2Service.clearTokens();
      return null;
    }
  }

  /**
   * Map OAuth2UserInfo to UserDTO
   */
  private mapUserInfoToDTO(userInfo: OAuth2UserInfo): UserDTO {
    return {
      id: userInfo.sub,
      name: userInfo.name,
      email: userInfo.email,
      username: userInfo.username,
      role: this.mapGroupsToRole(userInfo.groups || []),
      groups: userInfo.groups,
      sub: userInfo.sub,
    };
  }

  /**
   * Update the profile
   * @param data 
   * @returns 
   */
  async updateProfile(data: UpdateProfileRequestDTO): Promise<UserDTO> {
    // Mock de actualización
    await new Promise((resolve) => setTimeout(resolve, 500));

    const currentUser = await this.getCurrentUser();
    if (!currentUser) {
      throw new Error('Usuario no encontrado');
    }

    const updatedUser: UserDTO = {
      ...currentUser,
      name: data.nombres || currentUser.name,
      email: data.email || currentUser.email,
    };

    return updatedUser;

    // Implementación real:
    // return httpClient.put<UserDTO>(`${this.baseUrl}/profile`, data);
  }

  /**
   * Change the password
   * @param data 
   * @returns 
   */
  async changePassword(data: ChangePasswordRequestDTO): Promise<void> {
    // Mock de cambio de contraseña
    await new Promise((resolve) => setTimeout(resolve, 500));
    return;

    // Implementación real:
    // return httpClient.post<void>(`${this.baseUrl}/change-password`, data);
  }

  /**
   * Request password reset email
   * @param data - Email address
   * @returns Response with success and message
   */
  async forgotPassword(data: ForgotPasswordRequestDTO): Promise<ForgotPasswordResponseDTO> {
    const url = `${env.OAUTH2_BASE_URL}/auth/reset-password/request`;
    const response = await httpClient.post<ForgotPasswordResponseDTO>(url, data);
    return response;
  }

  /**
   * Reset password using reset token
   * @param data - Token and new password
   * @returns Response with success and message
   */
  async resetPassword(data: ResetPasswordRequestDTO): Promise<ResetPasswordResponseDTO> {
    const url = `${env.OAUTH2_BASE_URL}/auth/reset-password/complete`;
    const response = await httpClient.post<ResetPasswordResponseDTO>(url, data);
    return response;
  }
}

// instance singleton
export const authApi = new AuthApi();

